import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, X, Ticket, IndianRupee } from "lucide-react";

const emptyForm = { user_id: null, type: "voucher", points: "", shop_name: "", amount: "", note: "" };

export default function ManualRedemptionDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/users", { params: { q } }).then((r) => setUsers(r.data.filter((u) => u.role !== "admin")));
  }, [q, open]);

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const reset = () => { setSelected(null); setForm(emptyForm); setQ(""); };

  const submit = async () => {
    if (!selected) return toast.error("Select a user");
    const pts = parseInt(form.points, 10);
    if (!pts || pts <= 0) return toast.error("Enter valid points");
    if (form.type === "cash" && selected.role !== "affiliate") return toast.error("Cash payout is only allowed for affiliates");
    if (pts > selected.points_balance) return toast.error(`Only ${selected.points_balance} pts available`);
    setBusy(true);
    try {
      const payload = {
        user_id: selected.id,
        type: form.type,
        points: pts,
        note: form.note || null,
      };
      if (form.type === "voucher") payload.shop_name = form.shop_name || null;
      if (form.type === "cash" && form.amount) payload.amount = Number(form.amount);
      await api.post("/admin/redemptions/manual", payload);
      toast.success("Redemption logged — user's ledger updated");
      setOpen(false);
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="btn-log-redemption" className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Log redemption
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Log a redemption on behalf of a user</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-2 mb-2">Use this when you've already delivered the voucher offline or paid out cash. Points are deducted immediately and appear in the user's history.</p>

        <div className="space-y-4">
          {selected ? (
            <div className="flex items-center justify-between p-3 velo-surface rounded-lg">
              <div>
                <div className="text-sm text-white">{selected.name} <span className="text-xs text-slate-500 ml-2 capitalize">{selected.role}</span></div>
                <div className="text-xs text-slate-500 font-mono">{selected.phone} · Balance: {selected.points_balance} pts</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSelected(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Search user</Label>
                <div className="relative mt-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
                  <Input data-testid="input-redeem-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, phone or code" className="velo-input pl-10 h-11" />
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto velo-surface rounded-lg">
                {users.map((u) => (
                  <button key={u.id} data-testid={`redeem-user-${u.id}`} onClick={() => setSelected(u)} className="w-full text-left px-4 py-3 border-b border-white/5 flex items-center justify-between hover:bg-white/[0.03] transition-colors">
                    <div>
                      <div className="text-sm text-white">{u.name} <span className="text-xs text-slate-500 ml-1 capitalize">{u.role}</span></div>
                      <div className="text-xs text-slate-500 font-mono">{u.phone}</div>
                    </div>
                    <div className="text-xs font-mono text-slate-400">{u.points_balance} pts</div>
                  </button>
                ))}
                {users.length === 0 ? <div className="text-slate-500 text-center py-4 text-sm">No users</div> : null}
              </div>
            </>
          )}

          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider mb-2 block">Redemption type</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="btn-type-voucher"
                type="button"
                onClick={() => setField("type", "voucher")}
                className={`p-4 rounded-lg border text-left transition-colors ${form.type === "voucher" ? "border-pink-500 bg-pink-500/10" : "border-white/10 hover:border-white/20"}`}
              >
                <Ticket className="w-4 h-4 text-pink-400 mb-2" strokeWidth={1.5} />
                <div className="text-white text-sm font-medium">Tie-up shop</div>
                <div className="text-xs text-slate-500 mt-0.5">Voucher redemption</div>
              </button>
              <button
                data-testid="btn-type-cash"
                type="button"
                onClick={() => setField("type", "cash")}
                disabled={selected && selected.role !== "affiliate"}
                className={`p-4 rounded-lg border text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${form.type === "cash" ? "border-pink-500 bg-pink-500/10" : "border-white/10 hover:border-white/20"}`}
              >
                <IndianRupee className="w-4 h-4 text-pink-400 mb-2" strokeWidth={1.5} />
                <div className="text-white text-sm font-medium">Cash payout</div>
                <div className="text-xs text-slate-500 mt-0.5">Affiliates only</div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Points to redeem</Label>
              <Input data-testid="input-redeem-points" type="number" value={form.points} onChange={(e) => setField("points", e.target.value)} className="velo-input mt-2 h-11 font-mono" />
            </div>
            {form.type === "cash" ? (
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Amount paid (₹)</Label>
                <Input data-testid="input-redeem-amount" type="number" value={form.amount} onChange={(e) => setField("amount", e.target.value)} placeholder="Defaults to points × 1" className="velo-input mt-2 h-11 font-mono" />
              </div>
            ) : (
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Tie-up shop (optional)</Label>
                <Input data-testid="input-redeem-shop" value={form.shop_name} onChange={(e) => setField("shop_name", e.target.value)} placeholder="e.g. AutoWorld" className="velo-input mt-2 h-11" />
              </div>
            )}
          </div>

          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Note (optional)</Label>
            <Input data-testid="input-redeem-note" value={form.note} onChange={(e) => setField("note", e.target.value)} placeholder="e.g. UTR / cheque no." className="velo-input mt-2 h-11" />
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="btn-submit-redemption" onClick={submit} disabled={busy} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
            {busy ? "Saving…" : "Log redemption & deduct points"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
